<template>
  <div class="container">
    <div class="gallery">
      <div :key="imageIndex" style="position: relative" class="image-wrapper">
        <img
          v-if="$vuetify.breakpoint.lgAndUp && images.length > 2"
          :src="beforePreviousImage"
          class="item" />
        <img
          v-if="$vuetify.breakpoint.smAndUp && images.length > 1"
          :src="previousImage"
          class="item" />
        <img
          :src="
            images.length && loadImage
              ? images[imageIndex]
              : 'https://konselior.s3.amazonaws.com/media/semImagem.jpg'
          "
          class="item item-selected"
          @click="openZoomDialog"
          @error="loadImage = false" />
        <img
          v-if="$vuetify.breakpoint.smAndUp && images.length > 1"
          :src="nextImage"
          class="item" />
        <img
          v-if="$vuetify.breakpoint.lgAndUp && images.length > 2"
          :src="afterNextImage"
          class="item" />
        <v-btn
          v-if="loadImage"
          ref="zoomButton"
          icon
          class="zoom"
          @click="openZoomDialog">
          <v-icon large> $vuetify.icons.magnifyPlusIcon </v-icon>
        </v-btn>
      </div>
      <CarouselActionButtons :carousel="carousel" />
      <v-dialog
        ref="zoomDialog"
        v-model="showZoomDialog"
        transition="dialog-bottom-transition">
        <v-card>
          <v-btn fab small dark class="zoom-close" @click="closeZoomDialog">
            <v-icon large color="white"> mdi-close </v-icon>
          </v-btn>
          <v-img
            id="imageSelected"
            :src="
              images.length
                ? images[imageIndex]
                : 'https://konselior.s3.amazonaws.com/media/semImagem.jpg'
            "
            :alt="images.length ? `Imagem ${imageIndex + 1}` : 'Imagem ausente'"
            class="zoomed-image" />
          <CarouselActionButtons :carousel="carousel" />
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import { defineComponent } from '@vue/composition-api';
import Hammer from 'hammerjs';

export default defineComponent({
  props: {
    images: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      currentItem: 0,
      imageIndex: 0,
      showZoomDialog: false,
      hammer: null,
      imageElement: null,
      loadImage: true,
    };
  },

  computed: {
    previousImage() {
      return this.imageIndex === 0
        ? this.images[this.images.length - 1]
        : this.images[this.imageIndex - 1];
    },

    beforePreviousImage() {
      if (this.imageIndex === 0) {
        return this.images[this.images.length - 2];
      }

      if (this.imageIndex === 1) {
        return this.images[this.images.length - 1];
      }

      return this.images[this.imageIndex - 2];
    },

    nextImage() {
      return this.imageIndex === this.images.length - 1
        ? this.images[0]
        : this.images[this.imageIndex + 1];
    },

    afterNextImage() {
      if (this.imageIndex === this.images.length - 1) {
        return this.images[1];
      }

      if (this.imageIndex === this.images.length - 2) {
        return this.images[0];
      }

      return this.images[this.imageIndex + 2];
    },
  },

  watch: {
    imageIndex() {
      this.zoom();
    },
    showZoomDialog() {
      if (!this.showZoomDialog) this.closeZoomDialog();
    },
  },

  methods: {
    openZoomDialog() {
      this.showZoomDialog = true;
      this.zoom();
    },

    closeZoomDialog() {
      this.resetZoom();
      this.showZoomDialog = false;
    },

    zoom() {
      this.$nextTick(() => {
        this.imageElement = document.getElementById('imageSelected');
        let currentScale = 1;
        const maxScale = 2;
        let centerX = 0;
        let centerY = 0;
        let lastPosX = 0;
        let lastPosY = 0;
        let posX = 0;
        let posY = 0;
        let isPanning = false;

        this.hammer = new Hammer(this.imageElement);
        this.hammer.get('pan').set({ direction: Hammer.DIRECTION_ALL });
        this.hammer.get('pinch').set({ enable: true });

        this.hammer.on('pinchstart', (event) => {
          currentScale = this.imageElement.scale || 1;
          centerX =
            event.center.x - this.imageElement.getBoundingClientRect().left;
          centerY =
            event.center.y - this.imageElement.getBoundingClientRect().top;
        });

        this.hammer.on('pinch', (event) => {
          const { scale } = event;
          const newScale = currentScale * scale;

          if (newScale <= maxScale && newScale >= 1) {
            this.imageElement.style.transformOrigin = `${centerX}px ${centerY}px`;
            this.imageElement.style.transform = `scale(${newScale})`;
            this.imageElement.scale = newScale;
          }
        });

        this.hammer.on('pinchend', () => {
          currentScale = this.imageElement.scale || 1;
        });

        this.hammer.on('panstart', () => {
          isPanning = true;
          lastPosX = posX;
          lastPosY = posY;
        });

        this.hammer.on('pan', (event) => {
          if (isPanning && this.imageElement.scale > 1) {
            posX = lastPosX + event.deltaX;
            posY = lastPosY + event.deltaY;

            const maxOffsetX =
              (this.imageElement.offsetWidth * this.imageElement.scale -
                this.imageElement.offsetWidth) /
              2;
            const maxOffsetY =
              (this.imageElement.offsetHeight * this.imageElement.scale -
                this.imageElement.offsetHeight) /
              2;

            posX = Math.max(-maxOffsetX, Math.min(posX, maxOffsetX));
            posY = Math.max(-maxOffsetY, Math.min(posY, maxOffsetY));

            this.imageElement.style.transform = `scale(${this.imageElement.scale}) translate(${posX}px, ${posY}px)`;
          }
        });

        this.hammer.on('panend', () => {
          isPanning = false;
          lastPosX = posX;
          lastPosY = posY;
        });
      });
    },

    resetZoom() {
      if (this.imageElement) {
        this.imageElement.style.transform = 'scale(1)';
        this.imageElement.scale = 1;
      }
    },

    carousel(direction) {
      try {
        if (direction === 'left') {
          this.imageIndex -= 1;
        }

        if (direction === 'right') {
          this.imageIndex += 1;
        }

        if (this.imageIndex === this.images.length) {
          this.imageIndex = 0;
        }

        if (this.imageIndex < 0) {
          this.imageIndex = this.images.length - 1;
        }

        this.resetZoom();
      } catch (e) {
        console.log(e);
      }
    },
  },
});
</script>

<style lang="scss" scoped>
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.slide-fade-enter-active {
  transition: all 0.2s ease;
}
.slide-fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.5, 0.8, 1);
}
.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
}

.container {
  position: relative;
  padding: 20px;
  margin: 0 auto;
  max-width: 90vw;
}

.gallery {
  display: flex;
  flex-flow: row;
  justify-content: center;
  align-items: flex-end;
}

.item {
  @media (max-width: 559px) {
    max-width: 100%;
  }
  @media (min-width: 600px) {
    max-width: 30%;
    height: auto;
    flex-shrink: 0;
    transform: scale(0.7) translateY(30px);
    filter: brightness(70%);
  }
  @media (min-width: 1263px) {
    max-width: 20%;
  }
}

.item-selected {
  transform: none;
  filter: none;
  box-shadow: 0px 1px 4px rgb(0 0 0 / 5%);
}

.image-wrapper {
  position: relative;
  display: inline-block;
}

.zoom {
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(-130%, 10%);
}

.zoom-close {
  position: absolute;
  top: 10px;
  right: 10px;
  opacity: 0.3;
  z-index: 9999;
}
</style>
